import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar, Clock, MapPin, Users, Loader2, Trash2, Repeat, Bell, User } from "lucide-react";

const getDefaultDateTime = (addHours = 0) => {
  const now = new Date();
  now.setHours(now.getHours() + addHours, 0, 0, 0);
  return now.toISOString().slice(0, 16);
};

const RECURRENCE_OPTIONS = [
  { value: "none", label: "Keine Wiederholung" },
  { value: "daily", label: "Täglich" },
  { value: "weekly", label: "Wöchentlich" },
  { value: "monthly", label: "Monatlich" },
  { value: "yearly", label: "Jährlich" },
];

const REMINDER_OPTIONS = [
  { value: "0", label: "Keine Erinnerung" },
  { value: "15", label: "15 Minuten vorher" },
  { value: "30", label: "30 Minuten vorher" },
  { value: "60", label: "1 Stunde vorher" },
  { value: "1440", label: "1 Tag vorher" },
  { value: "2880", label: "2 Tage vorher" },
];

export function CalendarEventDialog({
  open,
  onOpenChange,
  onSubmit,
  onDelete,
  saving,
  deleting,
  editEvent = null,
  calendarOwners = [], // List of available calendar owners [{email, name}]
  defaultCalendarOwner = "", // Default selected calendar owner email
}) {
  const [formData, setFormData] = useState({
    subject: "",
    start_time: getDefaultDateTime(1),
    end_time: getDefaultDateTime(2),
    location: "",
    body: "",
    attendee_emails: "",
    recurrence: "none",
    reminder_minutes: "15",
    calendar_owner: defaultCalendarOwner,
  });

  // Reset form when dialog opens/closes or editEvent changes
  useEffect(() => {
    if (open) {
      if (editEvent) {
        setFormData({
          subject: editEvent.subject || "",
          start_time: editEvent.start?.slice(0, 16) || getDefaultDateTime(1),
          end_time: editEvent.end?.slice(0, 16) || getDefaultDateTime(2),
          location: editEvent.location || "",
          body: editEvent.body || "",
          attendee_emails: editEvent.attendees?.join(", ") || "",
          recurrence: editEvent.recurrence || "none",
          reminder_minutes: editEvent.reminder_minutes?.toString() || "15",
          calendar_owner: editEvent.calendarOwner || defaultCalendarOwner,
        });
      } else {
        setFormData({
          subject: "",
          start_time: getDefaultDateTime(1),
          end_time: getDefaultDateTime(2),
          location: "",
          body: "",
          attendee_emails: "",
          recurrence: "none",
          reminder_minutes: "15",
          calendar_owner: defaultCalendarOwner,
        });
      }
    }
  }, [open, editEvent, defaultCalendarOwner]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const attendees = formData.attendee_emails
      .split(",")
      .map(e => e.trim())
      .filter(e => e.includes("@"));
    
    onSubmit({
      ...formData,
      attendee_emails: attendees,
      event_id: editEvent?.id,
      reminder_minutes: parseInt(formData.reminder_minutes) || 0,
    });
  };

  const handleDelete = () => {
    if (editEvent?.id && onDelete) {
      onDelete(editEvent.id);
    }
  };

  const isEditing = !!editEvent;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            {isEditing ? "Termin bearbeiten" : "Neuer Kalender-Termin"}
          </DialogTitle>
          <DialogDescription>
            {isEditing 
              ? "Ändern Sie die Termindetails."
              : "Erstellen Sie einen neuen Termin in Ihrem Exchange-Kalender."
            }
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Calendar Owner Selection - only show if there are multiple owners and not editing */}
            {!isEditing && calendarOwners.length > 1 && (
              <div className="space-y-2">
                <Label htmlFor="calendar_owner" className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  Kalender zuordnen
                </Label>
                <Select
                  value={formData.calendar_owner}
                  onValueChange={(value) => setFormData({ ...formData, calendar_owner: value })}
                >
                  <SelectTrigger data-testid="event-calendar-owner-select">
                    <SelectValue placeholder="Kalender auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {calendarOwners.map((owner) => (
                      <SelectItem key={owner.email} value={owner.email}>
                        {owner.name || owner.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="subject">Betreff *</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Meeting-Titel"
                required
                data-testid="event-subject-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_time" className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Beginn *
                </Label>
                <Input
                  id="start_time"
                  type="datetime-local"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required
                  data-testid="event-start-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="end_time" className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Ende *
                </Label>
                <Input
                  id="end_time"
                  type="datetime-local"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  required
                  data-testid="event-end-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="location" className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                Ort
              </Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Besprechungsraum / Online"
                data-testid="event-location-input"
              />
            </div>
            
            {!isEditing && (
              <div className="space-y-2">
                <Label htmlFor="attendee_emails" className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  Teilnehmer (E-Mail-Adressen)
                </Label>
                <Input
                  id="attendee_emails"
                  value={formData.attendee_emails}
                  onChange={(e) => setFormData({ ...formData, attendee_emails: e.target.value })}
                  placeholder="person1@firma.de, person2@firma.de"
                  data-testid="event-attendees-input"
                />
                <p className="text-xs text-muted-foreground">
                  Mehrere E-Mail-Adressen mit Komma trennen
                </p>
              </div>
            )}

            {/* Recurrence and Reminder Section */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="recurrence" className="flex items-center gap-1">
                  <Repeat className="w-3 h-3" />
                  Wiederholung
                </Label>
                <Select
                  value={formData.recurrence}
                  onValueChange={(value) => setFormData({ ...formData, recurrence: value })}
                >
                  <SelectTrigger data-testid="event-recurrence-select">
                    <SelectValue placeholder="Wiederholung wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {RECURRENCE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reminder" className="flex items-center gap-1">
                  <Bell className="w-3 h-3" />
                  Erinnerung
                </Label>
                <Select
                  value={formData.reminder_minutes}
                  onValueChange={(value) => setFormData({ ...formData, reminder_minutes: value })}
                >
                  <SelectTrigger data-testid="event-reminder-select">
                    <SelectValue placeholder="Erinnerung wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {REMINDER_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="body">Beschreibung</Label>
              <Textarea
                id="body"
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                placeholder="Agenda, Notizen..."
                rows={3}
                data-testid="event-body-input"
              />
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {isEditing && onDelete && (
              <Button 
                type="button" 
                variant="destructive" 
                onClick={handleDelete}
                disabled={deleting}
                className="sm:mr-auto"
                data-testid="delete-event-btn"
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Löschen
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={saving} data-testid="save-event-btn">
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Calendar className="w-4 h-4 mr-2" />
              )}
              {saving ? "Speichern..." : isEditing ? "Speichern" : "Termin erstellen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
